import React, { useState } from 'react';
import styled from 'styled-components';
import { Button, Input, GhostButton, gtagEvent, vars } from '@agendor/ui';
import AuthService from './auth-service';

const CardBody = styled.div`
    padding: 24px;
`;

const FormGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 16px;
`;

const InputGroup = styled.label`
    display: flex;
    flex-direction: column;
    gap: 6px;
    font-weight: 600;
    font-size: 0.95rem;
    color: ${vars.foregroundColor};
`;

const ErrorText = styled.span`
    font-size: 0.85rem;
    font-weight: 400;
    color: ${vars.dangerColor};
`;

const ButtonGroup = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 16px;
`;

const MethodLink = styled(GhostButton).attrs({ hoverBg: 'bright', hoverBorder: false })`
    font-size: 0.85rem;
`;

const onlyDigits = (s: string) => s.replace(/\D/g, '');

const formatPhone = (value: string) => {
    const digits = onlyDigits(value);
    if (digits.length <= 10) {
        return digits.replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d)/, '$1-$2');
    }
    return digits.replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d)/, '$1-$2');
};

const isValidBrazilPhone = (value: string) => {
    const digits = onlyDigits(value);
    return digits.length === 11;
};

const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

type LoginStep = 'identifier' | 'code';
type AuthMethod = 'phone' | 'email';

interface LoginScreenProps {
    serverRootPath: string;
    authService: AuthService;
    onSuccess: () => void;
}

export default function LoginScreen({ serverRootPath, authService, onSuccess }: LoginScreenProps) {
    const [step, setStep] = useState<LoginStep>('identifier');
    const [method, setMethod] = useState<AuthMethod>('phone');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const normalizedPhone = '55' + onlyDigits(phoneNumber);
    const trimmedEmail = email.trim().toLowerCase();

    const isIdentifierValid = method === 'phone' ? isValidBrazilPhone(phoneNumber) : isValidEmail(email);

    const identifierDisplay = method === 'phone' ? phoneNumber : trimmedEmail;

    const handleRequestCode = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!isIdentifierValid) {
            setError(method === 'phone' ? 'Número de telefone inválido.' : 'E-mail inválido.');
            return;
        }

        gtagEvent('login_send_code_click', { type: method });
        setIsLoading(true);

        try {
            const body = method === 'phone' ? { phoneNumber: normalizedPhone } : { email: trimmedEmail };

            const response = await fetch(`${serverRootPath}/auth/request-code`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });

            if (response.status === 404) {
                setError(
                    method === 'phone'
                        ? 'Número não encontrado. Verifique se você já possui uma conta.'
                        : 'E-mail não encontrado. Verifique se você já possui uma conta.',
                );
                return;
            }
            if (response.status === 429) {
                setError('Muitas tentativas. Aguarde um momento.');
                return;
            }
            if (!response.ok) {
                setError('Erro ao enviar código. Tente novamente.');
                return;
            }

            setStep('code');
        } catch {
            setError('Erro de conexão. Verifique sua internet.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerifyCode = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        gtagEvent('login_verify_code_click', { type: method });
        setIsLoading(true);

        try {
            const body = method === 'phone' ? { phoneNumber: normalizedPhone, code } : { email: trimmedEmail, code };

            const response = await fetch(`${serverRootPath}/auth/verify-code`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
                credentials: 'include',
            });

            if (response.status === 401) {
                setError('Código inválido ou expirado.');
                return;
            }
            if (response.status === 429) {
                setError('Muitas tentativas. Aguarde um momento.');
                return;
            }
            if (!response.ok) {
                setError('Erro ao verificar código. Tente novamente.');
                return;
            }

            const { accessToken } = await response.json();
            authService.setAccessToken(accessToken);
            onSuccess();
        } catch {
            setError('Erro de conexão. Verifique sua internet.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleBack = () => {
        gtagEvent('login_back_click');
        setStep('identifier');
        setCode('');
        setError(null);
    };

    const handleToggleMethod = () => {
        const newMethod = method === 'phone' ? 'email' : 'phone';
        gtagEvent('login_toggle_method', { type: newMethod });
        setMethod(newMethod);
        setError(null);
    };

    return (
        <CardBody>
            <h2>Entrar</h2>
            {step === 'identifier' ? (
                <>
                    <p>
                        {method === 'phone'
                            ? 'Digite seu número de telefone para receber um código de acesso via WhatsApp.'
                            : 'Digite seu e-mail para receber um código de acesso.'}
                    </p>
                    <form onSubmit={handleRequestCode}>
                        <FormGroup>
                            {method === 'phone' ? (
                                <InputGroup>
                                    Telefone
                                    <Input
                                        id="phone"
                                        type="tel"
                                        placeholder="(00) 00000-0000"
                                        value={phoneNumber}
                                        onChange={(e) => setPhoneNumber(formatPhone(e.target.value))}
                                        disabled={isLoading}
                                        hasError={!!error}
                                        autoFocus
                                    />
                                    {error && <ErrorText>{error}</ErrorText>}
                                </InputGroup>
                            ) : (
                                <InputGroup>
                                    E-mail
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder="seu@email.com"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        disabled={isLoading}
                                        hasError={!!error}
                                        autoFocus
                                    />
                                    {error && <ErrorText>{error}</ErrorText>}
                                </InputGroup>
                            )}
                        </FormGroup>
                        <ButtonGroup>
                            <Button
                                type="submit"
                                primary
                                disabled={isLoading || !isIdentifierValid}
                                data-testid="send-code-btn"
                            >
                                {isLoading ? 'Enviando...' : 'Enviar código'}
                            </Button>
                        </ButtonGroup>
                    </form>
                    <div style={{ marginTop: '16px', textAlign: 'center' }}>
                        <MethodLink type="button" onClick={handleToggleMethod} data-testid="switch-auth-method">
                            {method === 'phone' ? 'Entrar com e-mail' : 'Entrar com telefone'}
                        </MethodLink>
                    </div>
                </>
            ) : (
                <>
                    <p>Digite o código de 6 dígitos enviado para {identifierDisplay}.</p>
                    <form id="verify-code-form" onSubmit={handleVerifyCode}>
                        <FormGroup>
                            <InputGroup>
                                Código
                                <Input
                                    id="code"
                                    type="text"
                                    inputMode="numeric"
                                    placeholder="000000"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    disabled={isLoading}
                                    hasError={!!error}
                                    autoFocus
                                />
                                {error && <ErrorText>{error}</ErrorText>}
                            </InputGroup>
                        </FormGroup>
                    </form>
                    <ButtonGroup>
                        <Button type="button" onClick={handleBack}>
                            Voltar
                        </Button>
                        <Button
                            type="submit"
                            form="verify-code-form"
                            primary
                            disabled={isLoading || code.length < 6}
                            data-testid="login-btn"
                        >
                            {isLoading ? 'Verificando...' : 'Entrar'}
                        </Button>
                    </ButtonGroup>
                </>
            )}
        </CardBody>
    );
}
